import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { projectsApi, type AddressReviewRow } from '@/lib/apiClient';
import JobAddressPicker from '../projects/JobAddressPicker';
import { isConflictError } from '@/lib/httpErrors';
import { invalidateAddressQueries } from '@/lib/queryClient';

/**
 * Job addresses still needing a verified location.
 *
 * ⚠️ THIS IS A WORKLIST, NOT A QUEUE OF SUGGESTIONS. An earlier version of this
 * screen showed what a nightly geocoding sweep had proposed and asked an admin to
 * confirm or reject it. Both halves of that were wrong: the sweep could not judge
 * a free-text address safely (six review rounds of counterexamples), and a screen
 * of pre-filled answers invites rubber-stamping, which is the failure mode you
 * least want on a value that sets per-diem pay.
 *
 * So there are no answers here — only the question. Each row carries the same
 * picker used on the project itself, so the whole backlog can be worked down the
 * page without navigating away, and every address that ends up verified is one a
 * person searched for and recognized. (#479.)
 */

const css = `
  .adr-intro { font-size: var(--ew-fs-body-sm); color: var(--ew-n-500); margin-bottom: 20px; max-width: 70ch; }
  .adr-empty { padding: 32px; text-align: center; color: var(--ew-n-500); font-size: var(--ew-fs-body-sm); }
  .adr-empty p { margin: 0 0 12px; }

  .adr-count {
    font: var(--ew-fw-semibold) var(--ew-fs-body-sm)/1.3 var(--ew-font);
    color: var(--ew-dark);
    margin: 0 0 8px;
  }
  .adr-count span { color: var(--ew-n-500); font-weight: 400; }

  .adr-row {
    display: grid;
    grid-template-columns: 150px minmax(0, 1fr);
    gap: var(--ew-s-3);
    align-items: start;
    padding: 14px 0;
    border-bottom: 1px solid var(--ew-n-100);
  }
  @media (max-width: 700px) {
    .adr-row { grid-template-columns: 1fr; gap: var(--ew-s-2); }
  }

  .adr-job { font-variant-numeric: tabular-nums; font-size: var(--ew-fs-body-sm); }
  .adr-job a { color: var(--ew-green); }
  .adr-title {
    display: block;
    font-size: var(--ew-fs-meta);
    color: var(--ew-n-500);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .adr-travel {
    display: inline-block;
    font-size: var(--ew-fs-meta);
    color: var(--ew-performance);
    border: 1px solid currentColor;
    border-radius: var(--ew-r-pill);
    padding: 0 6px;
    margin-top: 2px;
  }

  .adr-btn {
    padding: 6px var(--ew-s-3);
    border: 1px solid var(--ew-n-200);
    background: #fff;
    border-radius: var(--ew-r-md);
    font: inherit;
    font-size: var(--ew-fs-body-sm);
    cursor: pointer;
    min-height: 32px;
  }
  .adr-btn:hover { background: var(--ew-n-50); }
`;

export default function AddressReviewTab() {
  const qc = useQueryClient();

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['address-review'],
    queryFn: projectsApi.addressReview,
  });

  if (isLoading) return <div className="adr-empty">Loading…</div>;

  // ⚠️ A FAILED LOAD IS NOT AN EMPTY WORKLIST. `data ?? []` would render "nothing
  // waiting" — reporting every address settled during an API, auth or database
  // outage, which is the one wrong answer this screen must never give. (Codex, #479.)
  if (isError) {
    return (
      <>
        <style>{css}</style>
        <div className="adr-empty">
          <p>Could not load the list. Nothing has been changed.</p>
          <button
            type="button"
            className="adr-btn"
            onClick={() => { void refetch(); }}
            disabled={isFetching}
          >
            {isFetching ? 'Retrying…' : 'Try again'}
          </button>
        </div>
      </>
    );
  }

  const rows = data ?? [];

  if (!rows.length) {
    return (
      <>
        <style>{css}</style>
        <div className="adr-empty">
          <p>Every job address is verified. Nothing to do.</p>
        </div>
      </>
    );
  }

  const travel = rows.filter((r) => r.travelJob).length;

  const row = (r: AddressReviewRow) => (
    <div className="adr-row" key={r.projectId}>
      <div className="adr-job">
        <Link to={`/projects/${r.projectId}`}>{r.jobNumber ?? `#${r.projectId}`}</Link>
        <span className="adr-title">{r.title}</span>
        {r.travelJob ? <span className="adr-travel">Travel</span> : null}
      </div>

      {/* The same picker the project page uses, so there is one way to set an
          address and one write path behind it. It shows the stored text, and
          opens an empty search box when clicked. */}
      <JobAddressPicker
        value={r.storedAddress ?? ''}
        status={r.status === 'verified' ? 'verified' : 'unverified'}
        onPick={async (picked) => {
          try {
            await projectsApi.setAddress(r.projectId, {
              line1: picked.line1 ?? '',
              city: picked.city ?? '',
              state: picked.state ?? '',
              postalCode: picked.postalCode ?? undefined,
              latitude: picked.latitude,
              longitude: picked.longitude,
              type: picked.type,
              matchCodes: picked.matchCodes,
              // ⚠️ What this row said when the list was drawn. If a PM has since
              // changed or verified this job, the API refuses rather than letting
              // a stale row overwrite a location nobody here has seen.
              expect: { storedAddress: r.storedAddress },
            });
          } catch (e) {
            // A refused write means this row is describing a job that has moved
            // on. Re-read before the same stale row can be acted on again, then
            // rethrow so the picker reports that nothing was saved.
            if (isConflictError(e)) {
              await qc.invalidateQueries({ queryKey: ['address-review'] });
            }
            throw e;
          }
          // The row leaves the list on the next load, which is the feedback.
          // `invalidateAddressQueries` covers the worklist itself.
          await invalidateAddressQueries(qc, r.projectId);
        }}
        // ⚠️ NO onClear HERE ON PURPOSE — omitting it hides the Remove button.
        // Clearing writes by project id with no expectation, so a stale worklist
        // row could wipe an address a PM verified while this list sat open, and
        // take the per-diem coordinates with it. It is also a destructive action
        // taken from a row showing only a job number and some text. Removing an
        // address belongs on the project, which the job number links to.
        // (Codex, #479.)
      />
    </div>
  );

  return (
    <>
      <style>{css}</style>
      <p className="adr-intro">
        These jobs have no verified job site address. Search for each one and pick the
        match — the coordinates that come with it are what per-diem mileage is measured
        from, so the address has to be the real site rather than something close to it.
      </p>
      <h3 className="adr-count">
        {rows.length} to go <span>({travel} on travel jobs, listed first)</span>
      </h3>
      {rows.map(row)}
    </>
  );
}
